import { createTracker } from 'tracker';
import { Tracker } from 'gaw';
import axios from 'axios';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import {
    BrowserRouter as Router, Outlet,
    Route,
    Routes,
    useLocation
} from 'react-router-dom';
import { Locale } from 'locale';

import { getUserId, getUserPayload } from 'api';
import Loading from 'components/Loading';
import Info from './components/Info';
import { AccessUnavailable } from './components/AccessUnavailable';
import EditAuth from './components/EditAuth';
import EditPhoneNumber from './components/EditPhoneNumber';
import { Country, UserInfo } from './components';
import Alert, { AlertType } from './components/notification/Alert';
import Profile from './components/Profile';

export interface AlertMessage {
  content: string;
  type: AlertType;
}

export const AlertContext = createContext<
    (message: AlertMessage | null) => void
>(() => {
});

const fetchUserInfo = async () => {
  const userPayload = getUserPayload();
  if (userPayload == null) {
    throw new Error('`userPayload` is empty.');
  }
  const { accessToken: token, id } = userPayload;
  const config = {
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.get(`/mobile/users/${id}/`, config); // FIXME is response ok?
  const d = response.data.user;
  const { country } = new Locale(response.data.locale);
  const getCountry = (c: string) => {
    const map: { [c: string]: Country } = {
      KR: Country.KR,
      JP: Country.JP,
    };
    const found: Country | undefined = map[c];

    return found == null ? Country.KR : found;
  };
  const user: UserInfo = {
    id: d.id,
    uuid: d.uuid,
    phoneNumber:
            d['phone_accounts'].length !== 0
                ? d['phone_accounts'][0].phone_number
                : undefined,
    birthdaymmdd: d.birthdaymmdd != null ? d.birthdaymmdd : undefined,
    age: d.age != null ? d.age : undefined,
    gender: d.gender,
    country: getCountry(country),
    name: d.name,
  };

  return { token, user };
};

const tracker = createTracker();
const userId = getUserId();
if (userId != null) {
  Tracker.ga(`${tracker.fields.name}.set`, 'dimension1', userId);
}
export const trackerState = createContext<Tracker>(tracker);

interface ProtectedRoutesProps {
  userInfo: UserInfo;
  accessToken: string;
  updateUser: (user: UserInfo) => void;
  openUseAppForBestExperienceModal: boolean;
  setOpenUseAppForBestExperienceModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({
                                                             userInfo,
                                                             accessToken,
                                                             updateUser,
                                                             openUseAppForBestExperienceModal,
                                                             setOpenUseAppForBestExperienceModal,
                                                         }) => (
    <>
        <Routes>
            <Route
                path="/profile"
                element={
                    <Profile
                        userInfo={userInfo}
                        openUseAppForBestExperienceModal={openUseAppForBestExperienceModal}
                        onCloseUseAppForBestExperienceModal={() => setOpenUseAppForBestExperienceModal(false)}
                    />
                }
            />
            <Route
                path="/edit"
                element={
                    <Info
                        accessToken={accessToken}
                        userInfo={userInfo}
                        handleUserChanges={updateUser}
                    />
                }
            />
            <Route
                path="/phone-number"
                element={
                    <EditPhoneNumber
                        accessToken={accessToken}
                        userInfo={userInfo}
                        handleUserChanges={updateUser}
                    />
                }
            />
            <Route
                path="/auth"
                element={
                    <EditAuth
                        accessToken={accessToken}
                        userInfo={userInfo}
                    />
                }
            />
        </Routes>
        <Outlet />
    </>
);

const Main: React.FC = () => {
  const location = useLocation();
  const [isError, setIsError] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const [
        openUseAppForBestExperienceModal,
        setOpenUseAppForBestExperienceModal,
    ] = useState(true);

  useEffect(() => {
    axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL;
    const fetch = async () => {
      try {
        const payload = await fetchUserInfo();
        setUserInfo(payload.user);
        setAccessToken(payload.token);
        axios.defaults.headers.common[
                    'Authorization'
                    ] = `Bearer ${payload.token}`;
      } catch {
        setIsError(true);
        return;
      }
    };

    fetch();
  }, []);

  useEffect(() => {
    tracker.trackPage(location.pathname);
  }, [location.pathname]);

  const updateUser = (user: UserInfo) => {
    setUserInfo(user);
  };
  const [alertMessage, setAlertMessage] = useState<AlertMessage | null>(null);
  const handleRemoveAlert = useCallback(() => {
    setAlertMessage(null);
  }, []);
  const alert = alertMessage && (
        <Alert
            message={alertMessage.content}
            type={alertMessage.type}
            position="top"
            onRemove={handleRemoveAlert}
        />
    );


  return (
        <>
            {alert}
            <div id="modal" />
            <AlertContext.Provider value={setAlertMessage}>
                <Routes>
                    <Route
                        path="*"
                        element={
                            isError ? (
                                <AccessUnavailable />
                            ) : userInfo == null || accessToken == null ? (
                                <Loading />
                            ) : (
                                <ProtectedRoutes
                                    userInfo={userInfo}
                                    accessToken={accessToken}
                                    updateUser={updateUser}
                                    openUseAppForBestExperienceModal={openUseAppForBestExperienceModal}
                                    setOpenUseAppForBestExperienceModal={setOpenUseAppForBestExperienceModal}
                                />
                            )
                        }
                    />
                </Routes>
            </AlertContext.Provider>
        </>
  );
};

const App: React.FC = () => (
    <Router>
        <Main />
    </Router>
);

export default App;
